import { Result } from '../models/result'
import { Globals } from '../globals'
import { getRandomString } from '../utils/random.ts'
import { Logger, TaggedLogger } from '../logging/logger'
import { RequestMethod, RequestOptions, RequestResults } from './types'
import { PNException } from '../exceptions/pn-exception.ts'
import { ensureError } from '../utils/ensure-error.ts'
import { PNExceptionCode } from '../exceptions/pn-exception-code.ts'
import { delay } from '../utils/delay.ts'
import { isRetryable, retryAfterSecondsFrom, retryDelayMillis } from './backoff.ts'

/**
 * A request that got a response and still failed: a non-2xx the retry policy declined to re-send,
 * or a 2xx whose body was missing or unreadable. Carries the HTTP status so a caller that must know
 * whether the server received the request does not have to parse a message.
 *
 * @internal
 */
export class RequestResponseException extends PNException {
    public constructor(
        code: PNExceptionCode,
        public readonly httpStatus: number,
        originalError?: Error | null,
        details?: string,
    ) {
        super(code, originalError, details)
        // PNException pins its own prototype, so a subclass has to restore its own for instanceof.
        Object.setPrototypeOf(this, RequestResponseException.prototype)
    }
}

/**
 * The HTTP status a failed request received, or `undefined` when no response arrived at all.
 *
 * @internal
 */
export const httpStatusOf = (error: unknown): number | undefined =>
    error instanceof RequestResponseException ? error.httpStatus : undefined

/** @internal */
export const RequestException = {
    /** Invalid URL Exception Factory */
    invalidUrl: (url: string) => new PNException(PNExceptionCode.REQUEST_URL_INVALID, null, url),
    /** No Results Exception Factory */
    noResult: (httpStatus: number) => new RequestResponseException(PNExceptionCode.REQUEST_NO_RESULT, httpStatus),
    /** Non-2xx that the retry policy declined to re-send */
    unsuccessfulStatus: (status: number) =>
        new RequestResponseException(PNExceptionCode.REQUEST_FAILED, status, null, String(status)),
    /** Response Deserialization Failed Exception Factory */
    responseDeserializationFailed: (response: any, error: Error, httpStatus: number) =>
        new RequestResponseException(PNExceptionCode.REQUEST_DESERIALIZATION_FAILED, httpStatus, error, response),
    /** General Exception Factory */
    general: (error: Error) => new PNException(PNExceptionCode.REQUEST_FAILED, error),
} as const

type FetchRequestOptions = [url: URL, options: RequestInit & { headers: Record<string, string> }]

/** @internal */
export class Request {
    public readonly _id: string

    private readonly _method: RequestMethod
    private readonly _requestUrl: string
    private readonly _data: BodyInit

    private readonly _headers: Record<string, string>
    private readonly _parameters: Record<string, string>

    private readonly _logger: Logger

    private jitterMillis: number | null

    private readonly _timeoutMillis: number | null
    private readonly _maxAttempts: number
    private readonly _cacheMode: RequestCache | null

    public constructor(method: RequestMethod.GET, requestUrl: string)
    public constructor(method: RequestMethod.GET, requestUrl: string, data: null, options: RequestOptions)
    public constructor(method: RequestMethod.POST, requestUrl: string, data: any)
    public constructor(method: RequestMethod.POST, requestUrl: string, data: any, options: RequestOptions)
    public constructor(method: RequestMethod.POST, requestUrl: string, data: any, options: RequestOptions)
    public constructor(method: RequestMethod, requestUrl: string, data: any, options: RequestOptions, logger: Logger)
    public constructor(
        method: RequestMethod,
        requestUrl: string,
        data: any = null,
        options: RequestOptions = {},
        logger?: Logger,
    ) {
        this._logger = logger ?? TaggedLogger._getInstance('Request')

        this._id = getRandomString(5)
        this._method = method
        this._requestUrl = requestUrl
        this._data = data
        this._headers = {
            // For POST requests, explicit text/plain keeps the request CORS-simple (no preflight
            // OPTIONS). The body is JSON.stringify'd — the backend parses JSON regardless of
            // content type, and text/plain is a CORS-safelisted value (unlike application/json,
            // which triggers a preflight). Being explicit avoids relying on the browser's default.
            ...(method === RequestMethod.POST ? { 'Content-Type': 'text/plain' } : {}),
        }
        this._parameters = options.parameters ?? {}
        this.jitterMillis = options.jitterMillis ?? null
        this._timeoutMillis = options.timeoutMillis ?? null
        this._maxAttempts = options.maxAttempts ?? Globals._eventMaxSendAttempts
        this._cacheMode = options.cacheMode ?? null
    }

    public _getParameter(key: string): string | null {
        return this._parameters[key] ?? null
    }

    public _setParameter(key: string, value: string) {
        this._parameters[key] = value
    }

    public _removeParameter(key: string) {
        delete this._parameters[key]
    }

    public _getHeader(key: string): string | null {
        return this._headers[key] ?? null
    }

    public _setHeader(key: string, value: string) {
        this._headers[key] = value
    }

    public _removeHeader(key: string) {
        delete this._headers[key]
    }

    public _getJitterMillis(): number | null {
        return this.jitterMillis ?? null
    }

    public _setJitterMillis(jitterMillis: number) {
        this.jitterMillis = jitterMillis
    }

    public get _url(): Result<URL, PNException> {
        try {
            let url = new URL(this._requestUrl)
            if (Object.keys(this._parameters).length) {
                Object.entries(this._parameters).forEach(([key, value]) => {
                    url.searchParams.append(key, value)
                })
            }

            return Result.Ok(url)
        } catch {
            return Result.Err(RequestException.invalidUrl(this._requestUrl))
        }
    }

    public get _requestOptions(): Result<FetchRequestOptions, PNException> {
        const url = this._url
        if (url.isErr()) {
            return url
        }

        return Result.Ok([
            url.unwrap(),
            {
                method: this._method,
                headers: this._headers,
                body: this._data ? JSON.stringify(this._data) : undefined,

                // allows the request to outlive the page
                // https://developer.mozilla.org/en-US/docs/Web/API/fetch#keepalive
                keepalive: true,

                ...(this._cacheMode ? { cache: this._cacheMode } : {}),
            } as RequestInit & { headers: Record<string, string> },
        ])
    }

    public async _perform<T>(): Promise<Result<RequestResults<T>, PNException>> {
        return new Promise((complete) => {
            let startTime: Date
            let attemptCount = 0

            const getCompositeId = () => `(${this._id}-${attemptCount})`

            const logRequestStart = () => {
                startTime = new Date()
                attemptCount += 1
                this._logger.verbose(`${getCompositeId()} Opening ${this._requestUrl}`)
            }

            const logRequestClosed = () => {
                const endTime = new Date()
                const diff = (endTime.getTime() - startTime.getTime()) / 1000
                this._logger.verbose(`${getCompositeId()} Closed (${diff.toFixed(4)}) ${this._requestUrl}`)
            }

            /**
             * Schedules another attempt if the outcome is worth re-sending and the attempt budget
             * allows it, and reports whether it did.
             *
             * `status` is the response status, or omitted when the attempt produced no response at
             * all — a network failure or a timeout abort, which is retryable. `retryAfterSeconds`
             * is the delay the server asked for, when it asked for one.
             */
            const requeued = (status?: number, retryAfterSeconds?: number): boolean => {
                if (attemptCount >= this._maxAttempts) {
                    return false
                }

                if (!isRetryable(status)) {
                    return false
                }

                logRequestClosed()
                setTimeout(() => execute(), retryDelayMillis(attemptCount, retryAfterSeconds))
                return true
            }

            const execute = async (): Promise<void> => {
                logRequestStart()

                const controller = new AbortController()
                let timeoutTimer: ReturnType<typeof setTimeout> | undefined

                try {
                    const [url, fetchOptions] = this._requestOptions.unwrap()

                    if (this.jitterMillis) {
                        await delay(this.jitterMillis)
                    }

                    if (this._timeoutMillis !== null) {
                        timeoutTimer = setTimeout(() => controller.abort(), this._timeoutMillis)
                    }

                    const res = await fetch(url, { ...fetchOptions, signal: controller.signal })
                    if (!res.ok) {
                        const retryAfterSeconds = retryAfterSecondsFrom(res.status, res.headers.get('Retry-After'))
                        if (requeued(res.status, retryAfterSeconds)) {
                            return
                        }
                    }

                    if (res.status === 204) {
                        logRequestClosed()
                        complete(Result.Ok({ response: res }))
                        return
                    }

                    // A non-2xx that is not worth retrying is still a failure. Without this the
                    // outcome depended on whether the error body happened to parse as JSON: an
                    // XML or HTML body fell through to a deserialization error, but a JSON one
                    // was returned as a success carrying the error document as `data`, leaving
                    // every caller to infer failure from a payload it never type-checked.
                    if (!res.ok) {
                        logRequestClosed()
                        complete(Result.Err(RequestException.unsuccessfulStatus(res.status)))
                        return
                    }

                    const resBody = await res.text()
                    if (!resBody) {
                        logRequestClosed()
                        complete(Result.Err(RequestException.noResult(res.status)))
                        return
                    }

                    try {
                        logRequestClosed()
                        complete(Result.Ok({ data: JSON.parse(resBody), response: res }))
                    } catch (jsonError) {
                        this._logger.warn(`${getCompositeId()} Unable to decode response: ${resBody}`)
                        complete(
                            Result.Err(
                                RequestException.responseDeserializationFailed(
                                    resBody,
                                    ensureError(jsonError),
                                    res.status,
                                ),
                            ),
                        )
                    }
                } catch (err) {
                    // An aborted fetch surfaces here only after the abort has taken effect, so the
                    // error Result is returned after the abort completes (Req 6.3). Retry remains
                    // bounded by maxAttempts.
                    if (!requeued()) {
                        logRequestClosed()

                        const error = ensureError(err)
                        complete(Result.Err(RequestException.general(error)))
                    }
                } finally {
                    if (timeoutTimer) {
                        clearTimeout(timeoutTimer)
                    }
                }
            }

            execute()
        })
    }
}
